import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { useSelector } from 'react-redux';
import { RootState } from '../../redux/store';
import CircularProgress from '@mui/material/CircularProgress';
import { t } from 'i18next';
import SearchFilter from '../util/SearchFilter';

interface ModuleStat {
  module: string;
  totalSize: number;
  fileCount: number;
  lastUpload: string | null;
  path: string;
}

interface StorageStats {
  totalSize: number;
  modules: ModuleStat[];
}

interface Props {
  onOpenModule: (module: string) => void;
  onRefreshFiles?: () => void; // called after Refresh so Files tab re-fetches
}

const MODULE_CONFIG: Record<string, { color: string; icon: string; label: string }> = {
  documents:       { color: '#0891b2', icon: '📁', label: 'Documents' },
  disk:            { color: '#7c3aed', icon: '💾', label: 'Disk' },
  crm:             { color: '#2563eb', icon: '🤝', label: 'CRM' },
  hr:              { color: '#ec4899', icon: '👥', label: 'HR' },
  training:        { color: '#10b981', icon: '🎓', label: 'Training' },
  finance:         { color: '#d97706', icon: '💰', label: 'Finance' },
  avatars:         { color: '#8b5cf6', icon: '🖼️', label: 'Avatars' },
  driver_licenses: { color: '#0d9488', icon: '🪪', label: 'Driver Licenses' },
  other:           { color: '#9aaabf', icon: '📎', label: 'Other' },
};

export function getModuleCfg(mod: string) {
  return MODULE_CONFIG[mod] ?? MODULE_CONFIG['other'];
}

function formatSize(bytes: number) {
  if (bytes >= 1024 * 1024 * 1024) return `${(bytes / (1024 ** 3)).toFixed(1)} GB`;
  if (bytes >= 1024 * 1024) return `${(bytes / (1024 ** 2)).toFixed(0)} MB`;
  if (bytes >= 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${bytes} B`;
}

function formatDate(iso: string | null) {
  if (!iso) return '—';
  const d = new Date(iso);
  const today = new Date();
  if (d.toDateString() === today.toDateString()) {
    return `Today, ${d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
  }
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);
  if (d.toDateString() === yesterday.toDateString()) {
    return `Yesterday, ${d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
  }
  return d.toLocaleDateString([], { day: '2-digit', month: 'short', year: 'numeric' });
}

const DONUT_R = 38;
const DONUT_CIRCUMFERENCE = 2 * Math.PI * DONUT_R;

export default function DiskStorageView({ onOpenModule, onRefreshFiles }: Props) {
  const auth = useSelector((state: RootState) => state.auth.value);
  const [stats, setStats] = useState<StorageStats | null>(null);
  const [loading, setLoading] = useState(false);
  const [activeModule, setActiveModule] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [filterModule, setFilterModule] = useState<string | null>(null);

  const fetchStats = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get<StorageStats>('disk/storage-stats');
      setStats(data);
    } catch { /* handled globally */ } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (auth) fetchStats();
  }, [auth, fetchStats]);

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', paddingTop: 80 }}>
        <CircularProgress size={28} />
      </div>
    );
  }

  if (!stats) {
    return (
      <div style={{ textAlign: 'center', opacity: 0.35, fontSize: 13, paddingTop: 60 }}>
        {t('Empty')}
      </div>
    );
  }

  const totalSize = stats.totalSize || 1;

  // Build donut segments
  let offset = 0;
  const segments = stats.modules.map((m) => {
    const fraction = m.totalSize / totalSize;
    const dashLen = fraction * DONUT_CIRCUMFERENCE;
    const seg = { ...m, dashLen, dashOffset: -offset };
    offset += dashLen;
    return seg;
  });

  const filteredModules = stats.modules.filter((m) => {
    if (filterModule && m.module !== filterModule) return false;
    if (search) return m.module.toLowerCase().includes(search.toLowerCase()) || m.path.toLowerCase().includes(search.toLowerCase());
    return true;
  });

  const topModules = [...stats.modules].slice(0, 6);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16, padding: '16px 0' }}>

      {/* Summary row */}
      <div style={{ display: 'grid', gridTemplateColumns: '290px 1fr', gap: 16 }}>

        {/* Donut card */}
        <div style={{ background: 'var(--bg-form)', borderRadius: 5, border: '1px solid var(--border-input)', padding: 18 }}>
          <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase', color: 'var(--text-color-calendar-week)', marginBottom: 12 }}>
            {t('DiskTotalUsed')}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 14 }}>
            <svg width="88" height="88" viewBox="0 0 96 96" style={{ flexShrink: 0 }}>
              {/* track */}
              <circle cx="48" cy="48" r={DONUT_R} fill="none" stroke="var(--border-input)" strokeWidth="10"/>
              {segments.map((seg, i) => (
                <circle
                  key={i}
                  cx="48" cy="48" r={DONUT_R}
                  fill="none"
                  stroke={getModuleCfg(seg.module).color}
                  strokeWidth="10"
                  strokeDasharray={`${seg.dashLen} ${DONUT_CIRCUMFERENCE - seg.dashLen}`}
                  strokeDashoffset={seg.dashOffset}
                  strokeLinecap="round"
                  transform="rotate(-90 48 48)"
                />
              ))}
            </svg>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 22, fontWeight: 800, color: 'var(--text-color)', lineHeight: 1 }}>
                {formatSize(stats.totalSize)}
              </div>
              <div style={{ fontSize: 11, color: 'var(--text-color-calendar-week)', marginTop: 3 }}>
                {stats.modules.reduce((s, m) => s + m.fileCount, 0).toLocaleString()} files
              </div>
            </div>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
            {stats.modules.slice(0, 5).map((m) => {
              const cfg = getModuleCfg(m.module);
              return (
                <div key={m.module} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, color: 'var(--text-color-calendar)' }}>
                  <div style={{ width: 8, height: 8, borderRadius: '50%', background: cfg.color, flexShrink: 0 }} />
                  <span style={{ flex: 1 }}>{cfg.label}</span>
                  <span style={{ fontWeight: 600, color: 'var(--text-color)' }}>{formatSize(m.totalSize)}</span>
                </div>
              );
            })}
          </div>
        </div>

        {/* Module cards grid */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8 }}>
          {topModules.map((m) => {
            const cfg = getModuleCfg(m.module);
            const pct = Math.round((m.totalSize / totalSize) * 100);
            const isActive = activeModule === m.module;
            return (
              <div
                key={m.module}
                onClick={() => setActiveModule(isActive ? null : m.module)}
                style={{
                  background: isActive ? `${cfg.color}0d` : 'var(--btn-user-form)',
                  border: isActive ? `1.5px solid ${cfg.color}55` : '1px solid var(--border-input)',
                  borderRadius: 5,
                  padding: '12px 14px',
                  cursor: 'pointer',
                  transition: 'all .15s',
                }}
              >
                <div style={{ width: 32, height: 32, borderRadius: 8, background: `${cfg.color}1a`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, marginBottom: 8 }}>
                  {cfg.icon}
                </div>
                <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--text-color-calendar-week)', marginBottom: 2 }}>{cfg.label}</div>
                <div style={{ fontSize: 16, fontWeight: 800, color: 'var(--text-color)', lineHeight: 1 }}>{formatSize(m.totalSize)}</div>
                <div style={{ fontSize: 10, color: 'var(--text-color-calendar-week)', marginTop: 2 }}>{m.fileCount.toLocaleString()} files</div>
                <div style={{ marginTop: 8, height: 3, borderRadius: 3, background: 'var(--border-input)', overflow: 'hidden' }}>
                  <div style={{ width: `${pct}%`, height: '100%', background: cfg.color, borderRadius: 3 }} />
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Folders table */}
      <div style={{ background: 'var(--bg-form)', borderRadius: 5, border: '1px solid var(--border-input)', overflow: 'hidden' }}>
        {/* Header */}
        <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--border-input)', display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <div>
            <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-color)' }}>{t('DiskFolders')}</div>
            <div style={{ fontSize: 10, color: 'var(--text-color-calendar-week)' }}>{stats.modules.length} folders</div>
          </div>
          {/* Module filter chips */}
          <div style={{ display: 'flex', gap: 5, marginLeft: 8 }}>
            <button
              type="button"
              onClick={() => setFilterModule(null)}
              style={{
                padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 600, cursor: 'pointer',
                border: filterModule === null ? `1px solid rgba(8,145,178,.4)` : '1px solid var(--border-input)',
                background: filterModule === null ? 'rgba(8,145,178,.1)' : 'transparent',
                color: filterModule === null ? '#0891b2' : 'var(--text-color-calendar)',
                fontFamily: 'inherit',
              }}
            >
              All
            </button>
            {stats.modules.map((m) => {
              const cfg = getModuleCfg(m.module);
              const active = filterModule === m.module;
              return (
                <button
                  key={m.module}
                  type="button"
                  onClick={() => setFilterModule(active ? null : m.module)}
                  style={{
                    padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 600, cursor: 'pointer',
                    border: active ? `1px solid ${cfg.color}55` : '1px solid var(--border-input)',
                    background: active ? `${cfg.color}15` : 'transparent',
                    color: active ? cfg.color : 'var(--text-color-calendar)',
                    fontFamily: 'inherit',
                  }}
                >
                  {cfg.label}
                </button>
              );
            })}
          </div>
          {/* Search */}
          <div style={{ marginLeft: 'auto' }}>
            <SearchFilter columns={[]} search={search} onSearch={setSearch} filters={[]} onFilters={() => {}} />
          </div>
        </div>

        {/* Table */}
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              {['Folder', 'Files', 'Size', 'Last upload', ''].map((h) => (
                <th key={h} style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase', color: 'var(--text-color-calendar-week)', padding: '8px 14px', textAlign: 'left', borderBottom: '1px solid var(--border-input)', background: 'var(--background-color)', whiteSpace: 'nowrap' }}>
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filteredModules.length === 0 ? (
              <tr>
                <td colSpan={5} style={{ textAlign: 'center', padding: '32px', fontSize: 13, opacity: 0.35 }}>{t('Empty')}</td>
              </tr>
            ) : (
              filteredModules.map((m) => {
                const cfg = getModuleCfg(m.module);
                return (
                  <tr key={m.module} style={{ borderBottom: '1px solid var(--border-input)', transition: 'background .1s', cursor: 'default' }}
                    onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--btn-user-form)')}
                    onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
                  >
                    {/* Folder name */}
                    <td style={{ padding: '10px 14px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <div style={{ width: 28, height: 28, borderRadius: 7, background: `${cfg.color}15`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, flexShrink: 0 }}>
                          📂
                        </div>
                        <div>
                          <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-color)' }}>{cfg.label}</div>
                          <div style={{ fontSize: 10, color: 'var(--text-color-calendar-week)', marginTop: 1 }}>{m.path}</div>
                        </div>
                      </div>
                    </td>
                    {/* File count */}
                    <td style={{ padding: '10px 14px', fontSize: 13, color: 'var(--text-color-calendar)' }}>
                      {m.fileCount.toLocaleString()}
                    </td>
                    {/* Size */}
                    <td style={{ padding: '10px 14px', whiteSpace: 'nowrap' }}>
                      <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-color)' }}>{formatSize(m.totalSize)}</span>
                    </td>
                    {/* Last upload */}
                    <td style={{ padding: '10px 14px', fontSize: 12, color: 'var(--text-color-calendar-week)', whiteSpace: 'nowrap' }}>
                      {formatDate(m.lastUpload)}
                    </td>
                    {/* Open button */}
                    <td style={{ padding: '10px 14px', textAlign: 'right' }}>
                      <button
                        type="button"
                        onClick={() => onOpenModule(m.module)}
                        style={{ background: 'var(--btn-user-btn)', border: 'none', borderRadius: 5, padding: '4px 12px', fontSize: 11, color: 'var(--text-color)', cursor: 'pointer', fontFamily: 'inherit' }}
                        onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--btn-user-hover)')}
                        onMouseLeave={(e) => (e.currentTarget.style.background = 'var(--btn-user-btn)')}
                      >
                        Open
                      </button>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
